import { useMemo, useState, type ReactElement } from 'react';
import {
  useLoadTimeline,
  useLoadToday,
  useRecomputeLoad,
  type FormStatus,
  type LoadDay,
  type LoadTodayResponse,
} from '@/lib/load';
import { LoadChart } from './LoadChart';
import { MonthlyView } from './MonthlyView';
import { MonotonyTable } from './MonotonyTable';

type ViewMode = 'weekly' | 'monthly';

/**
 * `/carga` — vista PMC (Performance Management Chart).
 *
 * Decisiones de layout:
 * - Cabecera con título + botón "Recalcular ahora" alineado a la derecha.
 * - Selector de rango (30/90/180/365) como toolbar de botones segmentados,
 *   accesible vía `role="group"` + `aria-pressed` (es un selector
 *   single-select de UI común que un toggle group radio no encaja bien).
 * - Dos cards superiores: "Forma actual" (con color por estado) y "TSS última
 *   semana" (suma sobre el timeline cliente).
 * - Gráfica `LoadChart` en una `<section aria-label>` dedicada.
 */

const RANGE_DAYS = [30, 90, 180, 365] as const;
type RangeDays = (typeof RANGE_DAYS)[number];

export function LoadPage(): ReactElement {
  const [rangeDays, setRangeDays] = useState<RangeDays>(90);
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');

  // Calculamos las fechas en cliente: hoy y hoy-rangeDays.
  // `useMemo` para evitar cambiar la queryKey en cada render.
  const { fromISO, toISO } = useMemo(() => isoRange(rangeDays), [rangeDays]);

  const timelineQuery = useLoadTimeline(fromISO, toISO);
  const todayQuery = useLoadToday();
  const recomputeMutation = useRecomputeLoad();

  const days: LoadDay[] = timelineQuery.data?.days ?? [];

  // TSS última semana = suma de TSS de los últimos 7 días con datos.
  const lastWeekTss = useMemo(() => {
    if (days.length === 0) return 0;
    const slice = days.slice(-7);
    return slice.reduce((acc, d) => acc + (Number.isFinite(d.tss) ? d.tss : 0), 0);
  }, [days]);

  return (
    <section aria-labelledby="load-title" className="mx-auto max-w-6xl space-y-6">
      <Header
        onRecompute={() => recomputeMutation.mutate(undefined)}
        recomputePending={recomputeMutation.isPending}
        recomputeError={recomputeMutation.error?.message ?? null}
        recomputeSuccess={Boolean(recomputeMutation.data) && !recomputeMutation.isPending}
        recomputeDays={recomputeMutation.data?.recomputedDays ?? null}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <RangeSelector
        value={rangeDays}
        onChange={setRangeDays}
        disabled={timelineQuery.isLoading}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <TodayCard query={todayQuery} />
        <LastWeekCard tss={lastWeekTss} loading={timelineQuery.isLoading} />
      </div>

      <ChartSection
        days={days}
        isLoading={timelineQuery.isLoading}
        isError={timelineQuery.isError}
        errorMessage={timelineQuery.error?.message ?? null}
        rangeDays={rangeDays}
        viewMode={viewMode}
      />

      <MonotonyTable weeks={4} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({
  onRecompute,
  recomputePending,
  recomputeError,
  recomputeSuccess,
  recomputeDays,
  viewMode,
  onViewModeChange,
}: {
  onRecompute: () => void;
  recomputePending: boolean;
  recomputeError: string | null;
  recomputeSuccess: boolean;
  recomputeDays: number | null;
  viewMode: ViewMode;
  onViewModeChange: (m: ViewMode) => void;
}): ReactElement {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1
          id="load-title"
          className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
        >
          Carga (PMC)
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          CTL (forma a 42 días), ATL (fatiga a 7 días) y TSB (balance / forma).
        </p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div
          role="group"
          aria-label="Modo de vista"
          className="inline-flex gap-1 rounded-md bg-slate-100 p-1"
        >
          <button
            type="button"
            aria-pressed={viewMode === 'weekly'}
            onClick={() => onViewModeChange('weekly')}
            className={
              viewMode === 'weekly'
                ? 'rounded bg-teal-600 px-3 py-1 text-xs font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
                : 'rounded px-3 py-1 text-xs text-slate-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1'
            }
          >
            Vista semanal
          </button>
          <button
            type="button"
            aria-pressed={viewMode === 'monthly'}
            onClick={() => onViewModeChange('monthly')}
            className={
              viewMode === 'monthly'
                ? 'rounded bg-teal-600 px-3 py-1 text-xs font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
                : 'rounded px-3 py-1 text-xs text-slate-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1'
            }
          >
            Vista mensual
          </button>
        </div>
        <button
          type="button"
          onClick={onRecompute}
          disabled={recomputePending}
          aria-busy={recomputePending}
          className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {recomputePending ? 'Recalculando…' : 'Recalcular ahora'}
        </button>
        {recomputeError && (
          <p role="alert" className="text-xs text-rose-700">
            {recomputeError}
          </p>
        )}
        {recomputeSuccess && recomputeDays !== null && (
          <p role="status" aria-live="polite" className="text-xs text-emerald-700">
            Recalculados {recomputeDays} día{recomputeDays === 1 ? '' : 's'}.
          </p>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Selector de rango
// ---------------------------------------------------------------------------

function RangeSelector({
  value,
  onChange,
  disabled,
}: {
  value: RangeDays;
  onChange: (v: RangeDays) => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <div
      role="group"
      aria-label="Rango de fechas"
      className="inline-flex flex-wrap gap-2 rounded-md bg-slate-100 p-1"
    >
      {RANGE_DAYS.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(opt)}
            className={
              active
                ? 'rounded bg-teal-600 px-3 py-1 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
                : 'rounded px-3 py-1 text-sm text-slate-700 hover:bg-white hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60'
            }
          >
            {opt}d
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card: Forma actual
// ---------------------------------------------------------------------------

function TodayCard({
  query,
}: {
  query: ReturnType<typeof useLoadToday>;
}): ReactElement {
  return (
    <article
      aria-label="Forma actual"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Forma actual
      </h2>
      {query.isLoading && (
        <p role="status" aria-live="polite" className="mt-2 text-sm text-slate-600">
          Cargando…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="mt-2 text-sm text-rose-700">
          No se pudo cargar la forma actual.
        </p>
      )}
      {query.data && <TodayContent data={query.data} />}
    </article>
  );
}

function TodayContent({ data }: { data: LoadTodayResponse }): ReactElement {
  const cls = formStatusBadgeClass(data.formStatus);
  return (
    <div className="mt-2 space-y-3">
      <p className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${cls}`}
          aria-label={`Estado de forma: ${formStatusLabel(data.formStatus)}`}
          data-testid="today-form-status"
        >
          {formStatusLabel(data.formStatus)}
        </span>
        <span className="text-xs text-slate-500">{formStatusHint(data.formStatus)}</span>
      </p>
      <dl className="grid grid-cols-3 gap-2 text-sm">
        <Metric label="CTL" value={data.ctl.toFixed(1)} hint="forma 42d" />
        <Metric label="ATL" value={data.atl.toFixed(1)} hint="fatiga 7d" />
        <Metric label="TSB" value={data.tsb.toFixed(1)} hint="balance" />
      </dl>
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}): ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-lg font-semibold text-slate-900">{value}</dd>
      <p className="text-[10px] text-slate-500">{hint}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card: TSS última semana
// ---------------------------------------------------------------------------

function LastWeekCard({ tss, loading }: { tss: number; loading: boolean }): ReactElement {
  return (
    <article
      aria-label="TSS última semana"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        TSS última semana
      </h2>
      {loading ? (
        <p role="status" className="mt-2 text-sm text-slate-600">
          Cargando…
        </p>
      ) : (
        <p className="mt-2 text-3xl font-semibold text-slate-900" data-testid="lastweek-tss">
          {Math.round(tss)}
        </p>
      )}
      <p className="mt-1 text-xs text-slate-500">
        Suma del TSS diario de los últimos 7 días con datos.
      </p>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Chart section
// ---------------------------------------------------------------------------

function ChartSection({
  days,
  isLoading,
  isError,
  errorMessage,
  rangeDays,
  viewMode,
}: {
  days: LoadDay[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  rangeDays: number;
  viewMode: ViewMode;
}): ReactElement {
  return (
    <section
      aria-label="Gráfica PMC"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {viewMode === 'weekly'
          ? `PMC últimos ${rangeDays} días`
          : `Carga mensual (${rangeDays} días)`}
      </h2>
      <div className="mt-3">
        {isLoading && (
          <p role="status" aria-live="polite" className="text-sm text-slate-600">
            Cargando gráfica…
          </p>
        )}
        {isError && (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
            No se pudo cargar la gráfica. {errorMessage ?? ''}
          </p>
        )}
        {!isLoading && !isError && viewMode === 'weekly' && (
          <LoadChart
            days={days}
            ariaLabel={`PMC en los últimos ${rangeDays} días: CTL azul, ATL naranja, TSB sombreado por signo y barras grises de TSS diario.`}
          />
        )}
        {!isLoading && !isError && viewMode === 'monthly' && (
          <MonthlyView days={days} />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isoRange(rangeDays: number): { fromISO: string; toISO: string } {
  const now = new Date();
  // YYYY-MM-DD (UTC) — coincide con el formato `date_iso` que usa el backend
  // en `daily_load` (ver migración 0005 en el contrato).
  const toISO = now.toISOString().slice(0, 10);
  const from = new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000);
  const fromISO = from.toISOString().slice(0, 10);
  return { fromISO, toISO };
}

function formStatusLabel(s: FormStatus): string {
  switch (s) {
    case 'fresh': return 'Descansado';
    case 'fit': return 'En forma';
    case 'transition': return 'Transición';
    case 'overreaching': return 'Sobrecarga';
  }
}

function formStatusHint(s: FormStatus): string {
  switch (s) {
    case 'fresh': return 'TSB ≥ +25';
    case 'fit': return '-10 < TSB < +25';
    case 'transition': return '-30 < TSB ≤ -10';
    case 'overreaching': return 'TSB ≤ -30';
  }
}

function formStatusBadgeClass(s: FormStatus): string {
  // Contrastes WCAG AA: text-*-900 + bg-*-100 supera 4.5:1 para texto pequeño.
  switch (s) {
    case 'fresh':
      return 'bg-emerald-100 text-emerald-900';
    case 'fit':
      return 'bg-teal-100 text-teal-900';
    case 'transition':
      return 'bg-amber-100 text-amber-900';
    case 'overreaching':
      return 'bg-rose-100 text-rose-900';
  }
}
